import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useMatch, useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumber } from 'react-phone-number-input'
import { validateAddEditForm } from './validations';
import Header from './components/Header';
import Buttons from './components/Buttons';
import { Modules, AuthRoutes, RouteParamsTypes } from '../../../../enums';
import { VALIDATION_FIELD_MAPPING, APP_SUCCESS_MESSAGES, } from '../../../../utils/appConstant';
import { AddEdit as AddEditInterface, AddEditApi } from '../../../../interfaces/vendor.interface';
import { Validation } from '../../../../interfaces/common';
import VendorService from '../../../../services/vendor.service';
import CommonService from '../../../../services/common.service';
import Form from './components/Form';
import useFetch from '../../../../hooks/useFetch';
import useDebounce from '../../../../hooks/useDebounce';
import Spinner from '../../../../components/Spinner';
import Toast from '../../../../components/Toast';
import useUnsavedChangesWarning from '../../../../hooks/useUnsavedChangesWarning';
import ConfirmationFormRouting from '../../../../components/ConfirmationFormRouting';
import Modal from '../../../../components/Modal';
import Button from "../../../../components/Button";
import { STATUS_CODE } from '../../../../utils/apiConstant';

const initialFormData: AddEditInterface = {
  companyName: '',
  name: '',
  email: '',
  phoneNumber: '',
  phoneCountryCode: '',
  emergencyPhone: '',
  emergencyPhoneCountryCode: '',
  cities: [],
  serviceTypeId: '',
  status: true,
  notes: '',
  serviceRate: '',
}

const initialValidation: Validation = { validate: true, fields: [] };

const initialDropdownSearchData = {
  serviceTypeId: '',
  addressCountry: '',
  addressState: '',
  cities: ''
}

const AddEditView: React.FC = () => {
  const location = useLocation()
  const { t } = useTranslation();
  const { id } = useParams();
  const view = !!useMatch(Modules.resources + AuthRoutes.vendors + RouteParamsTypes.view + '/:id');
  const edit = !!useMatch(Modules.resources + AuthRoutes.vendors + RouteParamsTypes.edit + '/:id');
  const navigate = useNavigate();

  const [formData, setFormData] = useState<AddEditInterface>(initialFormData);
  const [dropdownSearchData, setDropdownSearchData] = useState<any>(initialDropdownSearchData);
  const [validation, setValidation] = useState<Validation>(initialValidation);
  const [loading, setLoading] = useState(false);
  const [actionTwoLoading, setActionTwoLoading] = useState<boolean>(false);
  const [suggestionSelected, setSuggestionSelected] = useState(false);
  const currentIdRef = useRef<string | undefined>(id);

  const [shouldBlock, setShouldBlock] = useState(!view);

  // Unsaved changes warning
  const { showPrompt, confirmNavigation, cancelNavigation } = useUnsavedChangesWarning({
    when: shouldBlock,
  });

  const debouncedVendorName = useDebounce(formData.name, 200);

  const { data: allServiceTypes } = useFetch({
    service: VendorService,
    func: 'serviceTypesLookup',
    payload: { searchText: '' },
    dependency: []
  });

  const { data: allCities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: { searchText: '' },
    dependency: []
  });

  const { data: cities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: { searchText: dropdownSearchData.cities },
    dependency: [dropdownSearchData.cities]
  });

  const { data: nameAutocompleteSuggestions } = useFetch({
    service: VendorService,
    func: 'lookup',
    payload: { searchText: debouncedVendorName, is_active: '' },
    dependency: [debouncedVendorName],
    skip: !!id || !debouncedVendorName || debouncedVendorName.trim().length < 2
  });

  useEffect(() => {
    currentIdRef.current = id;
    setFormData(initialFormData)
    setDropdownSearchData(initialDropdownSearchData)
    setValidation(initialValidation)
    setShouldBlock(!view);
    setSuggestionSelected(false);
  }, [location.key, id, view]);

  const { loading: getContentLoading } = useFetch({
    service: VendorService,
    func: 'get',
    payload: id,
    dependency: [id, location.key],
    skip: !id,
    onSuccess: (response: any) => {
      if (!currentIdRef.current) return;

      setFormData({
        companyName: response.company_name,
        name: response.name,
        email: response.email,
        phoneNumber: response.phone,
        phoneCountryCode: response.country_code || '',
        emergencyPhone: response.emergency_phone || '',
        emergencyPhoneCountryCode: response.emergency_country_code || '',
        cities: response.city_ids || [],
        serviceTypeId: response.service_type_id,
        status: response.is_active,
        notes: response.notes,
        serviceRate: response.service_rate?.toString(),
      });
    },
    onError: (error: any) => {
      if (error.code === STATUS_CODE.notFound) navigate(Modules.resources + AuthRoutes.vendors);
    }
  });

  const { data: serviceTypes } = useFetch({
    service: VendorService,
    func: 'serviceTypesLookup',
    payload: { searchText: dropdownSearchData.serviceTypeId },
    dependency: [dropdownSearchData.serviceTypeId]
  });

  const handleInputChange = useCallback((event: any, name: string) => {
    if (name === VALIDATION_FIELD_MAPPING.name) {
      setSuggestionSelected(false);
    }

    if (name === VALIDATION_FIELD_MAPPING.phoneNumber || name === VALIDATION_FIELD_MAPPING.emergencyPhone) {
      const phoneNumber = parsePhoneNumber(event?.target?.value ?? "")

      let objToUpdate: any;

      if (name === VALIDATION_FIELD_MAPPING.phoneNumber) {
        objToUpdate = {
          [VALIDATION_FIELD_MAPPING.phoneNumber]: phoneNumber && phoneNumber.nationalNumber,
          [VALIDATION_FIELD_MAPPING.phoneCountryCode]: phoneNumber ? `+${phoneNumber && phoneNumber.countryCallingCode}` : ""
        }
      } else if (name === VALIDATION_FIELD_MAPPING.emergencyPhone) {
        objToUpdate = {
          [VALIDATION_FIELD_MAPPING.emergencyPhone]: phoneNumber && phoneNumber.nationalNumber,
          [VALIDATION_FIELD_MAPPING.emergencyPhoneCountryCode]: phoneNumber ? `+${phoneNumber && phoneNumber.countryCallingCode}` : ""
        }
      }

      setFormData((prev) => {
        return {
          ...prev,
          ...objToUpdate
        };
      });
    } else {
      let value: any;

      if (name === VALIDATION_FIELD_MAPPING.status) {
        value = event.target.checked
      } else {
        value = event.target.value
      }

      setFormData((prev) => {
        return {
          ...prev,
          [name]: value
        };
      });
    }
  }, [setFormData]);

  const handleSuggestionSelect = useCallback(() => {
    setSuggestionSelected(true);
  }, []);

  const handleDropdownSearchInputChange = useCallback((event: any, name: string) => {
    setDropdownSearchData((prev: any) => ({
      ...prev,
      [name]: event.target.value,
    }));
  }, []);

  const handleInputBlur = useCallback((event: any, name: string) => {
    if (name === VALIDATION_FIELD_MAPPING.name) {
      setSuggestionSelected(true);
    }
    const newValidation = validateAddEditForm({
      ...formData,
      [name]: typeof event.target.value === 'string' ? event.target.value.trim() : event.target.value,
    }, name);

    setValidation(prev => {
      const filteredFields = prev.fields.filter(f => f.field !== name);
      const mergedFields = [...filteredFields, ...newValidation.fields];

      return {
        validate: mergedFields.length === 0,
        fields: mergedFields,
      };
    });
  }, [formData]);

  const handleSubmit = useCallback(async (action: '1' | '2', event?: React.FormEvent) => {
    event?.preventDefault();

    const newValidation = validateAddEditForm(formData);
    setValidation(newValidation);
    if (!newValidation.validate) return;

    if (action === '1') setLoading(true);
    else setActionTwoLoading(true);

    try {
      const payload: AddEditApi = {
        company_name: formData.companyName?.trim(),
        name: formData.name.trim(),
        email: formData.email?.trim(),
        phone: formData.phoneNumber?.trim(),
        country_code: formData.phoneCountryCode?.trim(),
        emergency_phone: formData.emergencyPhone?.trim(),
        emergency_country_code: formData.emergencyPhoneCountryCode?.trim(),
        address_city: formData.cities,
        service_type_id: formData.serviceTypeId,
        is_active: formData.status,
        notes: formData.notes?.trim(),
        service_rate: formData.serviceRate
      };

      let response: any;
      if (!edit) {
        response = await VendorService.add(payload);
      } else if (id) {
        response = await VendorService.edit(id, payload);
      }

      if (response.success !== false) {
        toast.success(<Toast message={id ? APP_SUCCESS_MESSAGES.vendorUpdated : APP_SUCCESS_MESSAGES.vendorCreated} />, { icon: false });

        setShouldBlock(false);
        setTimeout(() => {
          if (action === '2') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            const input = document.getElementById(VALIDATION_FIELD_MAPPING.name);
            input && input.focus();
            navigate(Modules.resources + AuthRoutes.vendors + RouteParamsTypes.add, { replace: true });
          } else {
            navigate(Modules.resources + AuthRoutes.vendors);
          }
        }, 0);
      }

      if (action === '1') setLoading(false);
      else setActionTwoLoading(false);
    } catch (err) {
      if (action === '1') setLoading(false);
      else setActionTwoLoading(false);
    }
  }, [id, edit, formData, navigate]);

  const handleCancel = () => {
    setShouldBlock(false);
    setTimeout(() => {
      navigate(Modules.resources + AuthRoutes.vendors);
    }, 0);
  };

  const handleKeyDown = useCallback((e: any, name?: string) => {
    if (name === VALIDATION_FIELD_MAPPING.notes) return;
    if (e.key === 'Enter') handleSubmit('1');
  }, [handleSubmit]);

  let formContent;
  if (getContentLoading) {
    formContent = (
      <div className='card-header text-center'>
        <Spinner />
      </div>
    )
  } else {
    formContent = (
      <Form
        serviceTypes={serviceTypes}
        cities={cities}
        allServiceTypes={allServiceTypes}
        allCities={allCities}
        view={view}
        dropdownSearchData={dropdownSearchData}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        formData={formData}
        validation={validation}
        nameAutocompleteSuggestions={suggestionSelected ? [] : nameAutocompleteSuggestions}
        onKeyDown={handleKeyDown}
        onInputBlur={handleInputBlur}
        onInputChange={handleInputChange}
        onSelect={handleSuggestionSelect}
      />
    )
  }

  return (
    <>
      <Header id={id} view={view} edit={edit} loading={getContentLoading} />

      <div className="card mb-6">
        <h5 className="card-header">{!id ? t("vendor.addEdit.pageTexts.title.addTitle") : (view ? t("vendor.addEdit.pageTexts.title.viewTitle") : t("vendor.addEdit.pageTexts.title.editTitle"))}</h5>

        <hr className="m-0" />

        {formContent}

        <hr className="m-0" />

        <Buttons
          loading={loading}
          view={view}
          edit={edit}
          actionTwoLoading={actionTwoLoading}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </div>

      <ConfirmationFormRouting
        open={showPrompt}
        onClose={cancelNavigation}
        onConfirm={confirmNavigation}
      />
    </>
  );
};

export default AddEditView;
